/**
 * Educational content for PNG to SVG page
 */

export const PngToSvgEducational = () => {
  return (
    <div className="space-y-8 mb-8">
      {/* Introduction Section */}
      <section className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold text-white mb-4">
          Convert PNG, JPG & Raster Images to Scalable Vector Graphics
        </h2>
        <p className="text-slate-300 mb-4">
          Transform your raster images (PNG, JPG, GIF, WebP, BMP) into infinitely scalable SVG vector graphics. Our 
          advanced image tracing algorithm analyzes your image pixel by pixel and converts it into mathematical paths 
          and shapes, creating a vector version that scales perfectly at any size without quality loss.
        </p>
        <p className="text-slate-300">
          Vector conversion is ideal for logos, icons, illustrations, and simple graphics where you need resolution 
          independence. Once converted to SVG, your images will look crisp on retina displays, large screens, and 
          when printed, while often being smaller in file size than high-resolution raster equivalents.
        </p>
      </section>

      {/* How It Works Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          How Image to Vector Conversion Works
        </h2>
        <p className="text-slate-300 mb-4">
          Converting raster images to vectors uses a process called "image tracing" or "vectorization." Here's how our 
          tool transforms pixels into paths:
        </p>
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <h3 className="font-medium text-white mb-2">1. Edge Detection</h3>
            <p className="text-slate-300 text-sm">
              The algorithm analyzes your image to identify edges and boundaries between different colors. It detects 
              where shapes begin and end by finding significant changes in pixel values and color transitions.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <h3 className="font-medium text-white mb-2">2. Path Creation</h3>
            <p className="text-slate-300 text-sm">
              Detected edges are converted into vector paths using Bézier curves. The algorithm determines optimal 
              anchor points and curve handles to recreate the shapes as smoothly as possible with minimal path complexity.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <h3 className="font-medium text-white mb-2">3. Color Grouping</h3>
            <p className="text-slate-300 text-sm">
              Similar colors are grouped together and converted into filled paths. The tool identifies distinct color 
              regions and creates separate shapes for each, making it easier to edit individual elements in the resulting SVG.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <h3 className="font-medium text-white mb-2">4. SVG Generation</h3>
            <p className="text-slate-300 text-sm">
              All paths and colors are compiled into a clean SVG file with organized structure. The output includes proper 
              viewBox settings, grouped elements, and optimized path data for the best balance of quality and file size.
            </p>
          </div>
        </div>
      </section>

      {/* Best Results Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          What Types of Images Work Best?
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-green-900/20 rounded-lg p-5 border border-green-700/50">
            <h3 className="text-lg font-medium text-green-400 mb-3">✓ Excellent Results</h3>
            <ul className="space-y-2 text-slate-300 text-sm">
              <li><strong>• Logos & Brand Marks:</strong> Clean designs with solid colors</li>
              <li><strong>• Icons & Symbols:</strong> Simple shapes and clear outlines</li>
              <li><strong>• Line Art:</strong> Drawings with distinct lines and minimal shading</li>
              <li><strong>• Illustrations:</strong> Artwork with flat colors and defined edges</li>
              <li><strong>• Silhouettes:</strong> High contrast images with clear subjects</li>
              <li><strong>• Text Images:</strong> Typography that needs to be scalable</li>
            </ul>
          </div>

          <div className="bg-red-900/20 rounded-lg p-5 border border-red-700/50">
            <h3 className="text-lg font-medium text-red-400 mb-3">⚠ Limited Results</h3>
            <ul className="space-y-2 text-slate-300 text-sm">
              <li><strong>• Photographs:</strong> Complex details don't vectorize well</li>
              <li><strong>• Gradients:</strong> Subtle color transitions create large SVG files</li>
              <li><strong>• Textures:</strong> Noise and grain result in thousands of tiny paths</li>
              <li><strong>• Blurry Images:</strong> Soft edges produce imprecise vector shapes</li>
              <li><strong>• Low Resolution:</strong> Pixelated sources create jagged vectors</li>
              <li><strong>• Complex Scenes:</strong> Detailed backgrounds with many elements</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Benefits of Vector Graphics
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">∞ Infinite Scalability</h3>
            <p className="text-slate-300 text-sm">
              Vector graphics remain sharp at any size—from tiny mobile icons to billboard-sized prints. No more creating 
              multiple resolution versions (@1x, @2x, @3x) of your images.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">📦 Smaller File Sizes</h3>
            <p className="text-slate-300 text-sm">
              Simple graphics as SVG are often 50-80% smaller than equivalent high-resolution PNG files, leading to faster 
              page loads and reduced bandwidth usage for your visitors.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">🎨 Easy Editing</h3>
            <p className="text-slate-300 text-sm">
              Change colors, modify shapes, and adjust sizing without degradation. Edit SVG files in vector editors like 
              Adobe Illustrator, Figma, or directly in code with text editors.
            </p>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Tips for Best Conversion Results
        </h2>
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Start with high-quality source images:</strong> Use the highest resolution 
                version available. Sharper, cleaner source images produce better vector conversions with smoother curves 
                and fewer artifacts.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Simplify before converting:</strong> Remove busy backgrounds and unnecessary 
                details in an image editor first. The simpler your source image, the cleaner and more usable your SVG will be.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Use high-contrast images:</strong> Images with clear distinction between 
                subject and background convert more accurately. Consider increasing contrast in photo editing software before 
                conversion.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Adjust color depth settings:</strong> Reducing the number of colors before 
                tracing produces simpler SVGs with fewer paths, making them easier to edit and smaller in file size.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Clean up the result:</strong> After conversion, review the SVG in a vector 
                editor. Remove stray points, merge overlapping shapes, and simplify paths to optimize the final output.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Use Cases Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Real-World Applications
        </h2>
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Logo Modernization</h3>
            <p className="text-slate-300 text-sm">
              Convert old logo image files to vectors for modern web use. This ensures your brand looks sharp on all 
              devices and can be recolored for different themes without requiring design software.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Icon Library Creation</h3>
            <p className="text-slate-300 text-sm">
              Transform PNG icons into SVG format for use in icon libraries. Vector icons can be scaled to any size, 
              colored with CSS, and used in responsive designs without managing multiple sizes.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Print-Ready Graphics</h3>
            <p className="text-slate-300 text-sm">
              Convert raster graphics to vectors for professional printing. Vector files can be scaled to any print size 
              without pixelation, making them ideal for business cards, posters, and merchandise.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Web Illustration</h3>
            <p className="text-slate-300 text-sm">
              Turn illustrations into editable SVG format for websites. This allows developers to animate elements, change 
              colors dynamically, and ensure graphics look perfect on high-DPI displays.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
